import { Button, IconButton, Tooltip } from "@mui/material";
import React, { useEffect, useState } from "react";
import {
  EVOKE_ROLES,
  TASK_STATUS_OPTIONS_COLOR_MAP,
  USER_ROLES,
} from "static/constants";

import { Add as AddIcon, Edit as EditIcon, Restore } from "@mui/icons-material";
import { toggleModal } from "app/store/transmission/slice/transmissionSlice";
import usePatientTasks from "../usePatientTasks";

import useDebounce from "app/hooks/useDebounce";
import { useFetchPatientTasksQuery } from "app/store/patientProfile/tasks/api/patientProfileTaskApi";
import CustomTable from "components/common/table/CustomTable";
import TaskCommunicationNote from "../TaskCommunicationNote";
import TaskNotesHistoryPopover from "components/pages/tasks/table/TaskNotesHistoryPopover";
import { textFilter } from "utils/table";
import formatDate from "utils/formatDate";

const PatientTaskTable = () => {
  const { currentUserRole, dispatch, patientId, profile } = usePatientTasks();
  const [columnVisibility, setColumnVisibility] = useState({});
  const [historyAnchor, setHistoryAnchor] = useState(null);
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());

  useEffect(() => {
    setColumnVisibility({
      ...columnVisibility,
      id: currentUserRole === USER_ROLES.CLINIC ? false : true,
    });
  }, []);

  const [search, setSearch] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const onSearchChange = useDebounce((value) => {
    setSearch(value || undefined);
  }, 800);

  const { data, isLoading, isFetching } = useFetchPatientTasksQuery(
    {
      patientId,
      page: pagination.pageIndex,
      pageSize: pagination.pageSize,
      search,
    },
    { refetchOnMountOrArgChange: true }
  );

  return (
    <CustomTable
      columns={[
        {
          header: "Date",
          accessorKey: "date",
          filterVariant: "date",
          filterFn: textFilter,
          size: 80,
          Cell: ({ row }) => {
            const rowData = row?.original;
            const { created_at } = rowData;
            return (
              <div>{created_at ? formatDate(rowData.created_at) : null}</div>
            );
          },
        },
        {
          header: "Account",
          accessorKey: "account",
          filterVariant: "text",
          size: 100,
        },
        {
          header: "Description",
          accessorKey: "description",
          filterVariant: "text",
          size: 100,
          Cell: ({ row }) => {
            const rowData = row?.original;
            const historyNotes = rowData?.notes ?? [];
            return (
              <div className="d-flex flex-column w-100 g-1">
                <Tooltip
                  style={{ fontSize: "18px", width: "300px" }}
                  title={rowData.description}
                  placement="bottom"
                >
                  <div
                    style={{
                      width: "200px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {rowData.description}
                  </div>
                </Tooltip>

                {rowData?.note?.note && (
                  <>
                    <TaskCommunicationNote
                      content={rowData?.note?.note}
                      addedAt={rowData?.note?.addedAt}
                      onExpand={() =>
                        setExpandedRowIds(
                          (prev) => new Set([...prev, rowData._id])
                        )
                      }
                      onCollapse={() =>
                        setExpandedRowIds((prev) => {
                          const next = new Set(prev);
                          next.delete(rowData._id);
                          return next;
                        })
                      }
                      action={
                        historyNotes.length > 0 && (
                          <span
                            role="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              e.preventDefault();
                              setHistoryAnchor({
                                rowId: rowData._id,
                                el: e.currentTarget,
                              });
                            }}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "4px",
                              color: "#94BA33",
                              fontFamily: "Roboto",
                              fontWeight: 500,
                              fontSize: "12px",
                              lineHeight: 1,
                              letterSpacing: 0,
                              whiteSpace: "nowrap",
                              cursor: "pointer",
                              verticalAlign: "middle",
                            }}
                          >
                            <Restore style={{ fontSize: "14px" }} />
                            View history ({historyNotes.length})
                          </span>
                        )
                      }
                    />
                    <TaskNotesHistoryPopover
                      anchorEl={historyAnchor?.el}
                      open={historyAnchor?.rowId === rowData._id}
                      onClose={() => setHistoryAnchor(null)}
                      patientName={profile?.name}
                      notes={historyNotes}
                    />
                  </>
                )}
              </div>
            );
          },
        },
        {
          header: "Status",
          accessorKey: "status",
          filterVariant: "text",
          size: 80,
          Cell: ({ row }) => {
            const rowData = row?.original;
            const color = TASK_STATUS_OPTIONS_COLOR_MAP[rowData.status];

            return (
              <span className={`task-status task-status--${color}`}>
                {rowData?.status}
              </span>
            );
          },
        },

        {
          accessorKey: "action",
          header: "Action",
          enableColumnFilter: false,
          size: 80,
          Cell: ({ row }) => {
            const rowData = row?.original;

            return (
              <div style={{ display: "flex" }}>
                <IconButton
                  // onClick={() => handleEdit(rowData)}
                  sx={{
                    "&:hover": {
                      color: "secondary.main",
                    },
                  }}
                >
                  <Tooltip title="Edit Task" placement="top">
                    <EditIcon data-action="edit-task-icon" />
                  </Tooltip>
                </IconButton>
              </div>
            );
          },
        },
      ]}
      data={data?.tasks || []}
      options={{
        enableTopToolbar: true,
        enableSorting: false,
        manualPagination: true,
        onGlobalFilterChange: onSearchChange,
        onPaginationChange: setPagination,
        rowsPerPageOptions: [10, 25, 100],
        state: {
          isLoading: isLoading || isFetching,
          showGlobalFilter: true,
          globalFilter: search,
          pagination,
          columnVisibility,
        },
        onColumnVisibilityChange: setColumnVisibility,
        renderTopToolbarCustomActions: () => (
          <div style={{ marginLeft: "5px" }}>
            <div style={{ fontSize: "21px", fontWeight: "500" }}>Tasks</div>

            {[...EVOKE_ROLES, USER_ROLES.CLINIC_ADMIN].includes(
              currentUserRole
            ) && (
              <div style={{ margin: "8px 0" }}>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    dispatch(
                      toggleModal({
                        type: "patientTask",
                        value: { open: true, action: "Add", task: {} },
                      })
                    )
                  }
                >
                  <AddIcon />
                  Add Task
                </Button>
              </div>
            )}
          </div>
        ),
        muiTableBodyCellProps: ({ row }) => ({
          sx: {
            ...(expandedRowIds.has(row.original?._id) && {
              verticalAlign: "top !important",
            }),
          },
        }),
        muiTableBodyRowProps: ({ row }) => {
          const isEvenRow = row.index % 2 !== 0;
          return {
            onClick: async () => {
              if (currentUserRole !== USER_ROLES.CLINIC) {
                await dispatch(
                  toggleModal({
                    type: "patientTask",
                    value: {
                      open: true,
                      action: "Edit",
                      task: row?.original,
                    },
                  })
                );
              }
            },
            sx: {
              ...(isEvenRow && {
                backgroundColor: "#F5F5F5",
                border: "1px solid #E0E0E0",
              }),
            },
          };
        },
      }}
    />
  );
};

export default PatientTaskTable;
